import React, { useState, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Download, Settings, Sparkles, QrCode, FileText, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';

const QRGeneratorModal = ({ isOpen, onClose, initialText = '', initialLocationName = '', discipline = '', disciplineColor = '' }) => {
  const [qrValue, setQRValue] = useState(initialText);
  const [inputValue, setInputValue] = useState(initialText);
  const [isGenerating, setIsGenerating] = useState(false);
  const [textBelow, setTextBelow] = useState(initialLocationName);
  const [resolution, setResolution] = useState(800); // Redusert fra 2000 til 800px for mindre filstørrelse
  const [showAdvanced, setShowAdvanced] = useState(false);
  const qrCodeRef = useRef(null);
  const previewSize = 240;

  // Auto-generate QR when modal opens with initial text
  React.useEffect(() => {
    if (isOpen && initialText) {
      setQRValue(initialText);
      setInputValue(initialText);
      setTextBelow(initialLocationName);
    }
  }, [isOpen, initialText, initialLocationName]);

  const generateQRCode = () => {
    if (!inputValue.trim()) return;
    setIsGenerating(true);
    setTimeout(() => {
      setQRValue(inputValue);
      setIsGenerating(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      generateQRCode();
    }
  };

  const generateQRCodeImage = useCallback(() => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = resolution;
      canvas.height = Math.round(resolution * 1.414);
      const ctx = canvas.getContext('2d');

      // Background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Header
      const headerHeight = canvas.height * 0.2;
      const headerMargin = resolution * 0.02;
      const headerRadius = resolution * 0.025;
      ctx.fillStyle = '#111827';
      // Fallback for browsers that don't support roundRect
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(headerMargin, headerMargin, canvas.width - 2 * headerMargin, headerHeight - headerMargin, [headerRadius, headerRadius, 0, 0]);
        ctx.fill();
      } else {
        // Fallback to regular rectangle
        ctx.fillRect(headerMargin, headerMargin, canvas.width - 2 * headerMargin, headerHeight - headerMargin);
      }
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const titleFontSize = resolution * 0.11;
      ctx.font = `900 ${titleFontSize}px Arial Black, sans-serif`;
      const titleY = headerMargin + (headerHeight - headerMargin) * 0.4;
      ctx.fillText('SKANN QR', canvas.width / 2, titleY);
      const subtitleFontSize = resolution * 0.05;
      ctx.font = `400 ${subtitleFontSize}px Arial, sans-serif`;
      const subtitleY = headerMargin + (headerHeight - headerMargin) * 0.75;
      ctx.fillText('Registrer treningen', canvas.width / 2, subtitleY);

      // QR positioning
      const qrAreaTop = headerHeight + resolution * 0.04;
      const availableHeight = canvas.height - headerHeight - (textBelow ? canvas.height * 0.1 : canvas.height * 0.04);
      const qrAreaMargin = resolution * 0.05;
      const maxSize = Math.min(availableHeight * 0.75, (canvas.width - 2 * qrAreaMargin) * 0.9);
      const qrSize = maxSize;
      const qrX = (canvas.width - qrSize) / 2;
      const qrY = qrAreaTop + (availableHeight - qrSize) / 2 - (textBelow ? resolution * 0.03 : 0);

      // Get SVG from preview
      const svgElement = qrCodeRef.current?.querySelector('svg');
      if (!svgElement) {
        resolve(canvas.toDataURL('image/png'));
        return;
      }
      const svgString = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

        // Text below QR code with optional discipline tag
        if (textBelow) {
          const textY = qrY + qrSize + resolution * 0.08;
          const tagText = discipline ? discipline.toUpperCase() : '';
          
          if (tagText) {
            // Dimensions - 50% larger tag than original
            const tagFontSize = resolution * 0.0525;  // 50% larger (was 0.035)
            const nameFontSize = resolution * 0.042;
            const tagPaddingX = resolution * 0.0225;   // 50% larger
            const tagPaddingY = resolution * 0.012;    // 50% larger
            const tagGap = resolution * 0.015;
            const maxNameWidth = canvas.width * 0.65;  // Max width for name text
            const lineHeight = nameFontSize * 1.25;    // Tight line spacing
            
            // Measure tag
            ctx.font = `700 ${tagFontSize}px Arial, sans-serif`;
            const tagTextWidth = ctx.measureText(tagText).width;
            const tagWidth = tagTextWidth + tagPaddingX * 2;
            const tagHeight = tagFontSize + tagPaddingY * 2;
            
            // Word-wrap the location name
            ctx.font = `400 ${nameFontSize}px Arial, sans-serif`;
            const words = textBelow.split(' ');
            const nameLines = [];
            let currentLine = '';
            
            for (const word of words) {
              const testLine = currentLine ? currentLine + ' ' + word : word;
              if (ctx.measureText(testLine).width > maxNameWidth && currentLine) {
                nameLines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            }
            if (currentLine) nameLines.push(currentLine);
            
            // Calculate widest name line for centering
            const widestLine = Math.max(...nameLines.map(l => ctx.measureText(l).width));
            const totalWidth = tagWidth + tagGap + widestLine;
            const startX = (canvas.width - totalWidth) / 2;
            
            // Vertically center tag with name block
            const nameBlockHeight = nameLines.length > 1 ? (nameLines.length - 1) * lineHeight + nameFontSize : nameFontSize;
            const tagCenterY = textY + nameBlockHeight / 2;
            
            // Draw pill-shaped tag background
            const tagX = startX;
            const tagY2 = tagCenterY - tagHeight / 2;
            const tagRadius = tagHeight / 2;
            
            ctx.fillStyle = disciplineColor || '#888888';
            ctx.beginPath();
            ctx.moveTo(tagX + tagRadius, tagY2);
            ctx.lineTo(tagX + tagWidth - tagRadius, tagY2);
            ctx.arcTo(tagX + tagWidth, tagY2, tagX + tagWidth, tagY2 + tagRadius, tagRadius);
            ctx.arcTo(tagX + tagWidth, tagY2 + tagHeight, tagX + tagWidth - tagRadius, tagY2 + tagHeight, tagRadius);
            ctx.lineTo(tagX + tagRadius, tagY2 + tagHeight);
            ctx.arcTo(tagX, tagY2 + tagHeight, tagX, tagY2 + tagRadius, tagRadius);
            ctx.arcTo(tagX, tagY2, tagX + tagRadius, tagY2, tagRadius);
            ctx.closePath();
            ctx.fill();
            
            // Draw tag text — dark text on light backgrounds for readability
            ctx.font = `700 ${tagFontSize}px Arial, sans-serif`;
            const dc = disciplineColor || '#888888';
            const rr = parseInt(dc.slice(1, 3), 16);
            const gg = parseInt(dc.slice(3, 5), 16);
            const bb = parseInt(dc.slice(5, 7), 16);
            const lum = rr * 0.299 + gg * 0.587 + bb * 0.114;
            ctx.fillStyle = lum > 160 ? '#000000' : '#FFFFFF';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(tagText, tagX + tagPaddingX, tagCenterY);
            
            // Draw wrapped location name lines after tag
            ctx.font = `400 ${nameFontSize}px Arial, sans-serif`;
            ctx.fillStyle = '#111827';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            const nameX = startX + tagWidth + tagGap;
            nameLines.forEach((line, i) => {
              ctx.fillText(line, nameX, textY + i * lineHeight);
            });
          } else {
            const textFontSize = resolution * 0.042;
            ctx.font = `400 ${textFontSize}px Arial, sans-serif`;
            ctx.fillStyle = '#111827';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(textBelow, canvas.width / 2, textY);
          }
        }
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(canvas.toDataURL('image/png'));
      img.src = 'data:image/svg+xml;base64,' + btoa(svgString);
    });
  }, [resolution, textBelow, discipline, disciplineColor]);

  const copyToClipboard = async () => {
    try {
      if (!qrValue) {
        alert("Generer en QR-kode først");
        return;
      }
      
      const dataUrl = await generateQRCodeImage();
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = await fetch(dataUrl).then(res => res.blob());
        await navigator.clipboard.write([new ClipboardItem({
          'image/png': blob
        })]);
        alert("QR-kode kopiert til utklippstavle!");
      } else {
        // Fallback - copy QR value as text
        await navigator.clipboard.writeText(qrValue);
        alert("QR-kode tekst kopiert til utklippstavle!");
      }
    } catch (err) {
      console.error('Copy failed:', err);
      // Final fallback - try copying text
      try {
        await navigator.clipboard.writeText(qrValue);
        alert("QR-kode tekst kopiert til utklippstavle!");
      } catch (textErr) {
        console.error('Text copy failed:', textErr);
        alert("Kunne ikke kopiere QR-kode. Prøv å høyreklikk og velg 'Kopier bilde'");
      }
    }
  };

  const saveAsPNG = async () => {
    try {
      const dataUrl = await generateQRCodeImage();
      const link = document.createElement('a');
      link.download = `qr-code-${textBelow ? textBelow.replace(/[^a-zA-Z0-9]/g, '-') : 'aktivlogg'}.png`;
      link.href = dataUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      alert("QR-kode lagret som PNG!");
    } catch (err) {
      console.error(err);
      alert("Kunne ikke lagre QR-kode");
    }
  };

  const saveAsPDF = async () => {
    try {
      const dataUrl = await generateQRCodeImage();
      
      // Konverter PNG til komprimert JPEG for mindre filstørrelse
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      const compressedDataUrl = await new Promise((resolve) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          // Konverter til JPEG med 80% kvalitet for bedre kompresjon
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = dataUrl;
      });
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [200, 300], // Custom format: 200x300mm (ikke A4!)
        compress: true, // Aktiverer PDF-kompresjon
        precision: 2    // Reduserer presisjon for mindre filer
      });

      // Use 90% of both 200x300mm dimensions
      const pageWidth = 200; // 200mm width as requested
      const pageHeight = 300; // 300mm height as requested
      const margin = 0.05; // 5% margin

      const imgWidth = pageWidth * (1 - 2 * margin);
      const imgHeight = pageHeight * (1 - 2 * margin);

      // Center the image on the page
      const x = pageWidth * margin;
      const y = pageHeight * margin;
      pdf.addImage(compressedDataUrl, 'JPEG', x, y, imgWidth, imgHeight, undefined, 'MEDIUM');

      // Add Aktivlogg text branding at bottom center (4cm width as requested)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8); // Small font for bottom branding
      pdf.setTextColor(0, 0, 0); // Black text
      
      const brandingText = 'AKTIVLOGG.no - digital treningslogg';
      const brandingWidth = pdf.getTextWidth(brandingText);
      const brandingX = (pageWidth - brandingWidth) / 2; // Center horizontally
      const brandingY = pageHeight - 15; // 15mm from bottom
      
      pdf.text(brandingText, brandingX, brandingY);

      pdf.save(`qr-code-${textBelow ? textBelow.replace(/[^a-zA-Z0-9]/g, '-') : 'aktivlogg'}.pdf`);
      alert("QR-kode lagret som PDF!");
    } catch (error) {
      console.error('PDF save error:', error);
      alert("Kunne ikke lagre QR-kode som PDF");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="bg-yellow-500/20 p-2 rounded mr-3">
              <QrCode className="h-6 w-6 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">QR-kode Generator</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Input Section */}
          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Lim inn din QR-kode-tekst her"
                className="w-full text-lg py-4 px-4 pr-12 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-all duration-300"
              />
              <Sparkles className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            
            <button
              onClick={generateQRCode}
              className="w-full py-4 text-lg font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              disabled={isGenerating || !inputValue.trim()}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin inline" />
                  Skaper magi...
                </>
              ) : (
                <>
                  <QrCode className="mr-2 h-5 w-5 inline" />
                  Generer QR-kode
                </>
              )}
            </button>
          </div>

          {/* Text below QR code */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Tekst under QR-kode</label>
            <input
              value={textBelow}
              onChange={(e) => setTextBelow(e.target.value)}
              placeholder="Skriv inn navnet på skytebanen som vil vises under QR-koden"
              className="w-full px-4 py-2 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-all duration-300"
            />
          </div>

          {/* Advanced Options */}
          <div className="border border-gray-600 rounded-lg">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full px-4 py-3 flex items-center text-gray-300 hover:text-white text-left"
            >
              <Settings className="w-4 h-4 mr-2" />
              <span className="font-medium">Avanserte alternativer</span>
              <span className="ml-auto">{showAdvanced ? '▲' : '▼'}</span>
            </button>
            {showAdvanced && (
              <div className="px-4 pb-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-700/50 border border-yellow-500/30 rounded-lg">
                  <label className="text-gray-300 font-medium">QR-kode oppløsning</label>
                  <div className="flex items-center space-x-2 flex-1">
                    <input
                      type="range"
                      min="100"
                      max="2000"
                      step="10"
                      value={resolution}
                      onChange={(e) => setResolution(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium min-w-[80px] text-gray-300">
                      {resolution}x{resolution}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* QR Code Preview */}
          {qrValue && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="bg-white rounded-lg shadow-lg border border-gray-600" style={{
                  width: `${previewSize + 48}px`
                }}>
                  {/* Header section matching export design */}
                  <div className="bg-gray-900 text-white rounded-t-lg px-6 py-4 text-center">
                    <div className="text-2xl font-black mb-1">SKANN QR</div>
                    <div className="text-sm font-normal">Registrer treningen</div>
                  </div>
                  
                  {/* QR Code area */}
                  <div className="px-8 py-6 flex flex-col items-center">
                    <div ref={qrCodeRef}>
                      <QRCodeSVG value={qrValue} size={previewSize} level="H" className="rounded-lg" />
                    </div>
                    {textBelow && (
                      <div className="flex items-center justify-center gap-2.5 mt-8 px-2">
                        {discipline && (
                          <span 
                            className="px-4 py-1.5 rounded-full text-white font-bold flex-shrink-0"
                            style={{ backgroundColor: disciplineColor || '#888', fontSize: '0.95rem' }}
                          >
                            {discipline.toUpperCase()}
                          </span>
                        )}
                        <span className="text-sm font-medium text-gray-900 text-left leading-tight">{textBelow}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-center gap-3 flex-wrap">
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-700 border-2 border-yellow-500/20 hover:border-yellow-500/40 hover:bg-gray-600 text-white rounded-lg transition-all duration-300"
                >
                  <Copy className="h-4 w-4" />
                  Kopier
                </button>
                <button
                  onClick={saveAsPNG}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-700 border-2 border-yellow-500/20 hover:border-yellow-500/40 hover:bg-gray-600 text-white rounded-lg transition-all duration-300"
                >
                  <Download className="h-4 w-4" />
                  PNG
                </button>
                <button
                  onClick={saveAsPDF}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-700 border-2 border-yellow-500/20 hover:border-yellow-500/40 hover:bg-gray-600 text-white rounded-lg transition-all duration-300"
                >
                  <FileText className="h-4 w-4" />
                  PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRGeneratorModal;